import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import { CompanyWorkPackageScopePanel } from './CompanyWorkPackageScopePanel';
import {
  companyFormSchema,
  companyPartyValues,
  type Company,
  type CompanyFormInput,
} from './types';
import { useCreateCompany, useUpdateCompany } from './useCompanies';

interface CompanyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row?: Company | null;
}

export const CompanyFormDialog = ({ open, onOpenChange, row }: CompanyFormDialogProps) => {
  const { t } = useTranslation('settings');
  const user = useCurrentUser();
  const create = useCreateCompany();
  const update = useUpdateCompany();
  const isEdit = !!row;
  const canManageScope =
    hasPermission(user, 'company.manage') &&
    (hasPermission(user, 'system.admin') || user?.partyType === 'platform');
  const form = useForm<CompanyFormInput>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      legal_name: '',
      display_name: '',
      party_type: 'supplier',
      registration_info: '',
      short_code: '',
      active_flag: true,
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset({
        legal_name: '',
        display_name: '',
        party_type: 'supplier',
        registration_info: '',
        active_flag: true,
      });
      return;
    }
    if (row) {
      form.reset({
        legal_name: row.legal_name,
        display_name: row.display_name,
        party_type: row.party_type,
        registration_info: row.registration_info ?? '',
        short_code: row.short_code ?? '',
        active_flag: row.active_flag,
      });
    }
  }, [form, open, row]);

  const submit = form.handleSubmit(async (values) => {
    if (isEdit && row) {
      await update.mutateAsync({
        id: row.company_id,
        body: {
          legal_name: values.legal_name.trim(),
          display_name: values.display_name.trim(),
          registration_info: values.registration_info?.trim() || undefined,
          short_code: values.short_code?.trim() || undefined,
          active_flag: values.active_flag,
        },
      });
    } else {
      await create.mutateAsync({
        legal_name: values.legal_name.trim(),
        display_name: values.display_name.trim(),
        party_type: values.party_type,
        registration_info: values.registration_info?.trim() || undefined,
        short_code: values.short_code?.trim() || undefined,
      });
    }
    onOpenChange(false);
  });
  const isPending = create.isPending || update.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEdit ? t('efs.settings.companies.edit') : t('efs.settings.companies.create')}
          </SheetTitle>
          <SheetDescription>{t('efs.settings.companies.create_subtitle')}</SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.companies.fields.display_name')}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="legal_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.companies.fields.legal_name')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="party_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.companies.fields.party_type')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={isEdit}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companyPartyValues.map((party) => (
                        <SelectItem key={party} value={party}>
                          {t(`efs.settings.companies.types.${party}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-efs-text-muted">
                    {t('efs.settings.companies.party_type_hint')}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="registration_info"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.companies.fields.registration_info')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="short_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.companies.fields.short_code')}</FormLabel>
                  <FormControl>
                    <Input maxLength={8} {...field} />
                  </FormControl>
                  <p className="text-xs text-efs-text-muted">
                    {t('efs.settings.companies.short_code_hint')}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isEdit ? (
              <FormField
                control={form.control}
                name="active_flag"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2 space-y-0">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                    </FormControl>
                    <FormLabel>{t('efs.settings.companies.fields.active')}</FormLabel>
                  </FormItem>
                )}
              />
            ) : null}
            {isEdit && row && canManageScope ? (
              <CompanyWorkPackageScopePanel companyId={row.company_id} />
            ) : null}
            <SheetFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEdit
                  ? t('efs.common.actions.save', { ns: 'common' })
                  : t('efs.settings.companies.actions.create')}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
